import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useProfile } from '@/lib/hooks/use-profile';
import { useFreelancerProfileId } from '@/lib/hooks/use-freelancer-profile-id';
import { useFreelancerProfile } from '@/lib/hooks/use-freelancer-profile';
import { useIsAdmin } from '@/lib/hooks/use-is-admin';

/**
 * Guard component that checks if a freelancer's profile is enabled.
 * If the profile is disabled, redirects to the disabled profile page.
 * Only applies to users who are freelancers (is_genius = true).
 * Admins bypass this check.
 */
export function ProfileEnabledGuard() {
  const location = useLocation();
  const { profile, isLoading: isLoadingProfile } = useProfile();
  const isAdmin = useIsAdmin();
  const { profileId: freelancerProfileId, isLoading: isLoadingProfileId } = useFreelancerProfileId(
    profile?.is_genius ? profile?.id : undefined
  );

  const {
    isEnabled,
    isLoading: isLoadingFreelancerProfile,
  } = useFreelancerProfile(freelancerProfileId ?? undefined, { enableMutations: false });

  const isDisabledPage = location.pathname === '/genius/disabled';

  // Show loading spinner while data is loading
  if (isLoadingProfile || isLoadingProfileId || (freelancerProfileId && isLoadingFreelancerProfile)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--color-4))]" />
      </div>
    );
  }

  // Only check for freelancers (is_genius = true)
  // Admins bypass this check
  if (profile?.is_genius && !isAdmin) {
    // If profile doesn't exist yet, allow access (user can create profile)
    if (!freelancerProfileId) {
      return <Outlet />;
    }

    // If profile is disabled and not on disabled page, redirect to disabled page
    if (!isEnabled && !isDisabledPage) {
      return <Navigate to="/genius/disabled" replace />;
    }

    // If profile is enabled and on disabled page, redirect to dashboard
    if (isEnabled && isDisabledPage) {
      return <Navigate to="/genius/dashboard" replace />;
    }
  }

  return <Outlet />;
}

